import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useAppUserAction } from "src/_common/hooks/actions/user/appUserActionHook";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { APPLICATION_GLOBAL_SLUG } from "src/_config";
import InputTextField from "src/components/common/Form/InputTextField";
import RegisterModal from "./RegisterModal";
import ForgotPasswordModal from "./ForgotPasswordModal";
import ResetPasswordModal from "./ResetPasswordModal";
import UpdateDisplayNameModal from "src/components/events/modal/updateDisplayNameModal";
import AuthModalHeader from "./AuthModalHeader";
import AuthModalFooter from "./AuthModalFooter";

interface EmailLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
  showResetPassword?: boolean; // Optional prop to directly show reset password modal
  resetToken?: string; // Optional reset token from email link
  onSwitchToPhone?: () => void; // Optional callback to switch to phone login
}

interface EmailLoginFormValues {
  email: string;
  password: string;
}

const emailLoginSchema = yup.object().shape({
  email: yup.string().email("Please enter a valid email").required("Email is required"),
  password: yup.string().required("Password is required"),
});

const EmailLoginModal: React.FC<EmailLoginModalProps> = ({
  isOpen,
  onClose,
  onLoginSuccess,
  showResetPassword = false,
  resetToken = "",
  onSwitchToPhone,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // const [passwordValue, setPasswordValue] = useState("");
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(showResetPassword);
  const [resetTokenState, setResetTokenState] = useState<string>(resetToken);
  const [showUpdateDisplayNameModal, setShowUpdateDisplayNameModal] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  // Hooks (same as loginWithEmail.tsx)
  const userApi = useUserApi();
  const toast = useToaster();
  const history = useHistory();
  const userAction = useAppUserAction();
  const userDetails = useAppUserDetailsSelector();

  // Check for display name after login
  useEffect(() => {
    if (justLoggedIn && userDetails) {
      setJustLoggedIn(false);

      if (!userDetails.display_name) {
        // User needs to set display name - show the modal
        setShowUpdateDisplayNameModal(true);
      } else {
        // User has display name - just close and call success
        onLoginSuccess();
      }
    }
  }, [justLoggedIn, userDetails]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    // register,
  } = useForm<EmailLoginFormValues>({
    resolver: yupResolver(emailLoginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = (data: EmailLoginFormValues) => {
    setIsLoading(true);

    // Prepare API data (same as loginWithEmail.tsx)
    const apiData = {
      ...data,
      game_slug: APPLICATION_GLOBAL_SLUG,
    };

    userApi.callLoginWithEmail(
      apiData,
      (message: string, resp: any) => {
        setIsLoading(false);
        if (resp) {
          userAction.setGuestUserId(null);

          // Mark that user just logged in to trigger display name check
          setJustLoggedIn(true);

          // Handle redirect if exists
          const link = localStorage.getItem("redirectLink");
          if (link) {
            localStorage.removeItem("redirectLink");
            history.push(link);
          }

          // Don't call onLoginSuccess immediately - wait for display name check
          // If display name exists or user closes the modal, then success will be called
        } else {
          toast.error(message);
        }
      },
      (message: string) => {
        setIsLoading(false);
        toast.error(message);
      },
    );
  };

  const handleClose = () => {
    reset();
    setIsLoading(false);
    setShowPassword(false);
    setRememberMe(false);
    setShowRegisterModal(false);
    setShowForgotPasswordModal(false);
    setShowResetPasswordModal(false);
    setShowUpdateDisplayNameModal(false);
    setJustLoggedIn(false);
    onClose();
  };

  const handleShowRegister = () => {
    setShowRegisterModal(true);
  };

  const handleRegisterClose = () => {
    setShowRegisterModal(false);
    onClose(); // Close the parent EmailLogin modal
  };

  const handleRegisterSuccess = () => {
    setShowRegisterModal(false);
    onLoginSuccess(); // Close both modals and trigger success
  };

  const handleSwitchToLogin = () => {
    setShowRegisterModal(false);
  };

  const handleShowForgotPassword = () => {
    setShowForgotPasswordModal(true);
  };

  const handleForgotPasswordClose = () => {
    setShowForgotPasswordModal(false);
    onClose(); // Close the parent EmailLogin modal
  };

  const handleBackToLogin = () => {
    setShowForgotPasswordModal(false);
    // Stay on the login modal
  };

  const handleForgotPasswordSuccess = (resetToken?: string) => {
    setShowForgotPasswordModal(false);
    // Show reset password modal with the token
    setResetTokenState(resetToken || "");
    setShowResetPasswordModal(true);
  };

  // const handleShowResetPassword = (token?: string) => {
  //   setResetTokenState(token || "");
  //   setShowResetPasswordModal(true);
  // };

  const handleResetPasswordClose = () => {
    setShowResetPasswordModal(false);
    setResetTokenState("");
    onClose(); // Close the parent EmailLogin modal
  };

  const handleResetPasswordSuccess = () => {
    setShowResetPasswordModal(false);
    setResetTokenState("");
    onLoginSuccess(); // Close all modals and trigger success
  };

  const handleSwitchToPhone = () => {
    if (onSwitchToPhone) {
      onSwitchToPhone();
    }
  };

  const handleUpdateDisplayNameClose = () => {
    setShowUpdateDisplayNameModal(false);
    // Call success callback after display name modal closes
    onLoginSuccess();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Only show EmailLogin modal when not showing sub-modals */}
      {!showRegisterModal && !showForgotPasswordModal && !showResetPasswordModal && !showUpdateDisplayNameModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content signin-modal">
                <div className="modal-header border-0">
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={handleClose}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body text-center">
                  {/* Header with Logo */}
                  <AuthModalHeader />
                  {/* Lock Icon */}
                  <div className="signin-icon mb-3">
                    <div className="lock-icon">
                      <i className="fas fa-lock"></i>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="signin-title mb-2">Sign In</h2>
                  <p className="signin-subtitle mb-4">Welcome to the arena</p>

                  <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Email Input */}
                    <div className="email-input-container mb-3">
                      <div className="input-group">
                        <span className="input-group-text email-modal-prefix">
                          <i className="fas fa-envelope"></i>
                        </span>
                        <InputTextField
                          fieldName="email"
                          control={control}
                          errors={errors}
                          placeholder="Enter your email or username"
                          fieldType="email"
                        />
                      </div>
                    </div>

                    {/* Password Input */}
                    <div className="password-input-container mb-3 position-relative">
                      <div className="input-group pb-3">
                        <span className="input-group-text email-modal-prefix">
                          <i className="fas fa-key"></i>
                        </span>
                        <div className="position-relative flex-grow-1">
                          <InputTextField
                            fieldName="password"
                            fieldType="password"
                            control={control}
                            errors={errors}
                            placeholder="Enter your password"
                            showPassword={showPassword}
                            toggleShowPassword={toggleShowPassword}
                            eyeStyle={{ top: "12px", right: "12px", zIndex: 10 }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Remember Me and Forgot Password */}
                    <div className="remember-forgot-container mb-3 d-flex justify-content-between align-items-center">
                      <div className="form-check">
                        <input
                          className="form-check-input email-modal-checkbox"
                          type="checkbox"
                          id="rememberMe"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <label className="form-check-label email-modal-remember-text" htmlFor="rememberMe">
                          Remember me
                        </label>
                      </div>
                      <button
                        type="button"
                        className="btn btn-link forgot-password-btn p-0"
                        onClick={handleShowForgotPassword}
                      >
                        Forgot password?
                      </button>
                    </div>

                    {/* Log In Button */}
                    <button
                      type="submit"
                      className="btn btn-success w-100 mb-3 email-modal-login-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Logging In...
                        </>
                      ) : (
                        "Log In"
                      )}
                    </button>
                  </form>

                  {/* Create Account Link */}
                  <div className="create-account-container">
                    <span className="create-account-text">New here? </span>
                    <button className="btn btn-link create-account-btn p-0" onClick={handleShowRegister}>
                      Create an account
                    </button>
                  </div>

                  {/* Phone Login Link */}
                  {onSwitchToPhone && (
                    <div className="phone-login-container mt-3">
                      <button className="btn btn-link phone-login-btn" onClick={handleSwitchToPhone}>
                        Login with phone
                      </button>
                    </div>
                  )}
                  {/* Footer Links */}
                  <AuthModalFooter />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Register Modal */}
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={handleRegisterClose}
        onRegisterSuccess={handleRegisterSuccess}
        onSwitchToLogin={handleSwitchToLogin}
      />

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={handleForgotPasswordClose}
        onBackToLogin={handleBackToLogin}
        onForgotPasswordSuccess={handleForgotPasswordSuccess}
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={handleResetPasswordClose}
        onResetSuccess={handleResetPasswordSuccess}
        token={resetTokenState}
      />

      {/* Update Display Name Modal */}
      <UpdateDisplayNameModal shouldShow={showUpdateDisplayNameModal} onClose={handleUpdateDisplayNameClose} />
    </>
  );
};

export default EmailLoginModal;
